// Phase 44 — PRICE-02 · D-09 · D-11 — <SuggestedPricePopover />.
//
// Popover body rendered inside the Radix PopoverContent opened by
// <SuggestedPriceChip>. Shows reasoning + up to 5 comp rows + [Dismiss][Apply]
// actions. Pattern-matches <ProvenancePopover /> (Phase 43 D-05/D-06).
//
// Security:
//   - Source links ALWAYS have `rel="noopener noreferrer"` + `target="_blank"`
//     (prevents window.opener reverse-tabnabbing; prevents Referer leak).
//   - Reasoning string is rendered via React escaping — NEVER
//     dangerouslySetInnerHTML — mitigates T-44-05 XSS via agent-authored text.
//
// Typography lock (UI-SPEC §Typography):
//   16px font-semibold heading · 14px font-normal body · 13px font-normal
//   label-tier + action buttons. NO text-xs, NO font-medium anywhere.

import { Sparkles, ArrowUpRight } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { ScrollArea } from '@marine/ui/scroll-area';
import type { MarketBandComp } from '@marine/contracts/types/ai.types';

export interface SuggestedPricePopoverProps {
  suggestedPrice: number | null;
  reasoning: string;
  sourceComps: MarketBandComp[];
  onApply: () => void;
  onDismiss: () => void;
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function CompRow({ comp }: { comp: MarketBandComp }) {
  const year = comp.year ?? '';
  const make = comp.make ?? '';
  const model = comp.model ?? '';
  const description = [year, make, model].filter(Boolean).join(' ').trim() || '—';
  const similarityPct = Math.round(comp.similarity * 100);

  return (
    <li className="flex items-center gap-4 text-[0.8125rem] font-normal">
      <span className="text-sm font-normal">${formatCurrency(comp.price)}</span>
      <span className="text-muted-foreground" aria-hidden="true">·</span>
      <span className="text-[0.8125rem] font-normal flex-1 truncate">
        {description}
      </span>
      <span className="text-muted-foreground" aria-hidden="true">·</span>
      <span className="text-[0.8125rem] font-normal">{similarityPct}%</span>
      <span className="text-muted-foreground" aria-hidden="true">·</span>
      {comp.sourceUrl ? (
        <a
          href={comp.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open source listing for ${description} in new tab`}
          className="text-primary inline-flex items-center gap-1 text-[0.8125rem] font-normal hover:underline"
        >
          Source
          <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
        </a>
      ) : (
        <span className="text-muted-foreground text-[0.8125rem] font-normal" aria-hidden="true">
          —
        </span>
      )}
    </li>
  );
}

/**
 * Phase 44 — PRICE-02 · D-09 · D-11.
 *
 * Review surface for a market-band suggestion. Rendered inside
 * `<PopoverContent>` by `<SuggestedPriceChip>` when the user clicks the chip
 * body (not the inline Apply button).
 */
export function SuggestedPricePopover({
  suggestedPrice,
  reasoning,
  sourceComps,
  onApply,
  onDismiss,
}: SuggestedPricePopoverProps) {
  // Cap to top 5 by similarity. Agent should already return ≤ 5 (D-01
  // sourceComps cap) but defend in depth.
  const topComps = [...sourceComps]
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 5);

  const priceForAria = suggestedPrice !== null ? `$${formatCurrency(suggestedPrice)}` : '';

  return (
    <div className="space-y-4 max-w-[400px]">
      {/* 16px font-semibold heading */}
      <div className="flex items-center gap-2">
        <Sparkles
          className="h-4 w-4 text-blue-600 dark:text-blue-400"
          role="img"
          aria-hidden="true"
        />
        <h3 className="text-base font-semibold">Suggested price</h3>
      </div>

      {/* 14px font-normal body. React escapes reasoning automatically — T-44-05
          XSS mitigation. Empty / whitespace-only falls back to placeholder. */}
      <p className="text-sm font-normal text-foreground">
        {reasoning && reasoning.trim().length > 0 ? reasoning : 'No reasoning available'}
      </p>

      {/* Top comparables list */}
      {topComps.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[0.8125rem] font-normal text-muted-foreground">
            Top comparables
          </h4>
          {topComps.length > 3 ? (
            <ScrollArea className="max-h-[200px]">
              <ol role="list" className="space-y-2">
                {topComps.map((c) => (
                  <CompRow key={c.id} comp={c} />
                ))}
              </ol>
            </ScrollArea>
          ) : (
            <ol role="list" className="space-y-2">
              {topComps.map((c) => (
                <CompRow key={c.id} comp={c} />
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Action row — right-aligned Dismiss then Apply (Tab order matches) */}
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onDismiss}
          aria-label="Dismiss suggestion"
          data-testid="suggested-price-popover-dismiss"
          className="text-[0.8125rem] font-normal"
        >
          Dismiss
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={onApply}
          aria-label={
            priceForAria
              ? `Apply suggested price ${priceForAria}`
              : 'Apply suggested price'
          }
          data-testid="suggested-price-popover-apply"
          className="text-[0.8125rem] font-normal"
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
